import React, { useState, useEffect, useCallback } from 'react';
import { appCfgApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useLookups } from '../../context/LookupContext';
import {
  List, Plus, Save, Search, Database, Table2, RefreshCw
} from 'lucide-react';

const LookupValuesPage = () => {
  const { notify } = useNotifications();
  const { refresh: refreshLookupContext } = useLookups();

  // Data
  const [categoryMeta, setCategoryMeta] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editedValues, setEditedValues] = useState({});
  const [newValue, setNewValue] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Category config edit state (for view/query types)
  const [categoryEdits, setCategoryEdits] = useState(null);
  const [dbViews, setDbViews] = useState([]);
  const [viewColumns, setViewColumns] = useState([]);

  const selectedCatMeta = categoryMeta.find(c => c.category === selectedCategory);
  const sourceType = selectedCatMeta?.type || 'static';

  const loadCategories = useCallback(async () => {
    try {
      const res = await appCfgApi.getAllLookupCategories();
      setCategoryMeta(res.data);
    } catch (err) {
      notify.error('Failed to load lookup categories');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadValues = useCallback(async (category) => {
    if (!category) return;
    try {
      const res = await appCfgApi.getLookupsByCategory(category);
      setValues(res.data);
      setEditedValues({});
    } catch (err) {
      notify.error(`Failed to load values for ${category}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadCategories();
      try {
        const viewsRes = await appCfgApi.getDatabaseViews();
        setDbViews(viewsRes.data);
      } catch (err) { /* views list is optional */ }
      setLoading(false);
    };
    init();
  }, [loadCategories]);

  useEffect(() => {
    if (selectedCategory) {
      const meta = categoryMeta.find(c => c.category === selectedCategory);
      if (meta?.type === 'static') {
        loadValues(selectedCategory);
      } else {
        setValues([]);
        setEditedValues({});
      }
      setCategoryEdits(meta ? { ...meta } : null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, categoryMeta]);

  useEffect(() => {
    const loadColumns = async () => {
      if (categoryEdits?.type === 'view' && categoryEdits?.view_Name) {
        try {
          const res = await appCfgApi.getViewColumns(categoryEdits.view_Name);
          setViewColumns(res.data);
        } catch (err) {
          setViewColumns([]);
        }
      } else {
        setViewColumns([]);
      }
    };
    loadColumns();
  }, [categoryEdits?.type, categoryEdits?.view_Name]);

  useEffect(() => {
    if (categoryMeta.length > 0 && !selectedCategory) {
      setSelectedCategory(categoryMeta[0].category);
    }
  }, [categoryMeta, selectedCategory]);

  const handleFieldChange = (id, field, value) => {
    setEditedValues(prev => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: value }
    }));
  };

  const getFieldValue = (item, field) => {
    if (editedValues[item.id]?.[field] !== undefined) return editedValues[item.id][field];
    return item[field];
  };

  const hasEdits = Object.keys(editedValues).length > 0;

  const hasCategoryEdits = categoryEdits && selectedCatMeta && (
    categoryEdits.type !== selectedCatMeta.type ||
    categoryEdits.validation !== selectedCatMeta.validation ||
    categoryEdits.view_Name !== selectedCatMeta.view_Name ||
    categoryEdits.view_Column !== selectedCatMeta.view_Column
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const [id, changes] of Object.entries(editedValues)) {
        const original = values.find(v => v.id === parseInt(id));
        if (!original) continue;
        await appCfgApi.updateLookup(id, { ...original, ...changes });
      }
      notify.success('Lookup values saved');
      setEditedValues({});
      await loadValues(selectedCategory);
      await refreshLookupContext();
    } catch (err) {
      notify.error('Failed to save: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCategoryConfig = async () => {
    if (!categoryEdits) return;
    setSaving(true);
    try {
      await appCfgApi.updateLookupCategory(selectedCategory, categoryEdits);
      notify.success(`Category "${selectedCategory}" config saved`);
      await loadCategories();
      await refreshLookupContext();
      if (categoryEdits.type === 'static') await loadValues(selectedCategory);
    } catch (err) {
      notify.error('Failed to save category config: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAddValue = async () => {
    if (!newValue.trim() || !selectedCategory) return;
    try {
      const maxOrder = values.length > 0
        ? Math.max(...values.map(v => v.display_Order)) + 1
        : 1;
      await appCfgApi.createLookup({
        category: selectedCategory,
        value: newValue.trim(),
        display_Order: maxOrder,
        is_Active: true
      });
      setNewValue('');
      notify.success(`Added "${newValue.trim()}" to ${selectedCategory}`);
      await loadValues(selectedCategory);
      await refreshLookupContext();
    } catch (err) {
      notify.error('Failed to add value: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    const catName = newCategory.trim().replace(/\s+/g, '');
    try {
      await appCfgApi.createLookupCategory({
        category: catName,
        type: 'static',
        view_Name: null,
        view_Column: null,
        query: null
      });
      await appCfgApi.createLookup({
        category: catName,
        value: '(default)',
        display_Order: 1,
        is_Active: true
      });
      setNewCategory('');
      notify.success(`Category "${catName}" created`);
      await loadCategories();
      setSelectedCategory(catName);
      await refreshLookupContext();
    } catch (err) {
      notify.error('Failed to create category: ' + (err.response?.data?.message || err.message));
    }
  };

  const sourceTypeIcon = (type) => {
    if (type === 'view') return <Database size={12} />;
    if (type === 'query') return <Table2 size={12} />;
    return null;
  };

  const categories = categoryMeta.map(c => c.category);
  const filteredCategories = categoryMeta.filter(c =>
    !searchTerm || c.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="stui-module-loading">Loading...</div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <List size={20} />
          <h1>Lookup Values</h1>
          <span className="stui-module-header-meta">{categories.length} categories</span>
        </div>
        <div className="stui-module-header-actions">
          {sourceType === 'static' && (
            <button className="stui-crud-btn" onClick={handleSave} disabled={saving || !hasEdits} title="Save Values">
              <Save size={18} />
            </button>
          )}
          {hasCategoryEdits && (
            <button className="stui-crud-btn" onClick={handleSaveCategoryConfig} disabled={saving} title="Save Config">
              <Save size={18} />
            </button>
          )}
          <button
            className="stui-crud-btn"
            onClick={async () => {
              await loadCategories();
              if (selectedCategory) await loadValues(selectedCategory);
              await refreshLookupContext();
              notify.success('Refreshed');
            }}
            title="Refresh"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      <div className="stui-list-view">
        {/* Category List */}
        <div className="stui-list-panel">
          <div className="stui-list-panel-header">
            <div className="stui-search-wrapper">
              <Search size={14} className="stui-search-icon" />
              <input
                type="text"
                placeholder="Filter categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="stui-field-input"
              />
            </div>
          </div>

          <div className="stui-list-scroll">
            {filteredCategories.map(cat => (
              <button
                key={cat.category}
                className={`stui-list-item ${selectedCategory === cat.category ? 'selected' : ''}`}
                onClick={() => setSelectedCategory(cat.category)}
              >
                <span className="stui-list-item-primary">{cat.category}</span>
                {cat.type !== 'static' && (
                  <span className={`stui-badge-${cat.type}`}>
                    {sourceTypeIcon(cat.type)} {cat.type}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="stui-list-add-row">
            <input
              type="text"
              placeholder="New category name..."
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
              className="stui-field-input"
            />
            <button
              className="stui-btn stui-btn-secondary stui-btn-sm"
              onClick={handleAddCategory}
              disabled={!newCategory.trim()}
              title="Add category"
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Values Panel */}
        <div className="stui-detail-panel">
          {selectedCategory ? (
            <>
              <div className="stui-detail-header">
                <div className="stui-module-header-left">
                  <h3>{selectedCategory}</h3>
                  <span className="stui-module-header-meta">
                    {sourceType === 'static' ? `${values.length} values` : `source: ${sourceType}`}
                  </span>
                </div>
                <select
                  className="stui-field-input"
                  value={categoryEdits?.type || 'static'}
                  onChange={(e) => setCategoryEdits(prev => ({
                    ...prev,
                    type: e.target.value,
                    view_Name: e.target.value === 'static' ? null : prev?.view_Name,
                    view_Column: e.target.value === 'static' ? null : prev?.view_Column
                  }))}
                >
                  <option value="static">Static</option>
                  <option value="view">SQL View</option>
                  <option value="query">SQL Query</option>
                </select>
              </div>

              {/* View config */}
              {categoryEdits?.type === 'view' && (
                <div>
                  <div className="stui-field-group stui-label-left">
                    <label>View / Table Name:</label>
                    <select
                      value={categoryEdits?.view_Name || ''}
                      onChange={(e) => setCategoryEdits(prev => ({ ...prev, view_Name: e.target.value || null }))}
                      className="stui-field-input"
                    >
                      <option value="">-- Select View --</option>
                      {dbViews.map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div className="stui-field-group stui-label-left">
                    <label>Column:</label>
                    <select
                      value={categoryEdits?.view_Column || ''}
                      onChange={(e) => setCategoryEdits(prev => ({ ...prev, view_Column: e.target.value || null }))}
                      className="stui-field-input"
                      disabled={!categoryEdits?.view_Name}
                    >
                      <option value="">{categoryEdits?.view_Name ? '-- Select Column --' : '-- Select a view first --'}</option>
                      {viewColumns.map(col => <option key={col} value={col}>{col}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* Query config */}
              {categoryEdits?.type === 'query' && (
                <div>
                  <div className="stui-field-group stui-label-left">
                    <label>SQL Query:</label>
                    <textarea
                      value={categoryEdits?.query || ''}
                      onChange={(e) => setCategoryEdits(prev => ({ ...prev, query: e.target.value || null }))}
                      className="stui-field-input"
                      placeholder="SELECT DISTINCT Column_Name FROM Table_Name ORDER BY Column_Name"
                      rows={3}
                    />
                  </div>
                  <p className="stui-hint">⚠ Query support coming soon</p>
                </div>
              )}

              {/* Static values table */}
              {(categoryEdits?.type === 'static' || sourceType === 'static') && categoryEdits?.type !== 'view' && categoryEdits?.type !== 'query' && (
                <>
                  <div className="stui-data-grid-scroll-area">
                    <table className="stui-data-grid">
                      <thead>
                        <tr>
                          <th>Order</th>
                          <th>Value</th>
                          <th>Active</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values.map(item => (
                          <tr key={item.id} className={!getFieldValue(item, 'is_Active') ? 'stui-inactive' : ''}>
                            <td>
                              <input
                                type="number"
                                value={getFieldValue(item, 'display_Order')}
                                onChange={(e) => handleFieldChange(item.id, 'display_Order', parseInt(e.target.value) || 0)}
                                className="stui-field-input"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={getFieldValue(item, 'value')}
                                onChange={(e) => handleFieldChange(item.id, 'value', e.target.value)}
                                className="stui-field-input"
                              />
                            </td>
                            <td>
                              <label className="stui-toggle">
                                <input
                                  type="checkbox"
                                  checked={getFieldValue(item, 'is_Active')}
                                  onChange={() => handleFieldChange(item.id, 'is_Active', !getFieldValue(item, 'is_Active'))}
                                />
                                <span className="stui-toggle-slider" />
                              </label>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="stui-list-add-row">
                    <input
                      type="text"
                      placeholder="Add new value..."
                      value={newValue}
                      onChange={(e) => setNewValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddValue()}
                      className="stui-field-input"
                    />
                    <button
                      className="stui-btn stui-btn-secondary stui-btn-sm"
                      onClick={handleAddValue}
                      disabled={!newValue.trim()}
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="stui-detail-empty">Select a category to view values</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LookupValuesPage;
